import { CircularProgress, Alert, IconButton, Button } from "@mui/material"
import { Formik } from "formik"
import { X } from "lucide-mui"
import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import * as Yup from "yup"

import BasicInfo from "@/components/basic_info"
import ContactInformation from "@/components/settings/contact_information"
import Description from "@/components/settings/description"
import Location from "@/components/settings/location"
import VenuePhotos from "@/components/settings/photos"
import { ReservationRuleSections, type Section } from "@/components/settings/reservation_rules"
import SocialAccounts from "@/components/settings/social_accounts"
import VenueLogo from "@/components/settings/venue_logo"
import i18n from "@/i18n"
import { usePanelLink } from "@/shared/hooks"
import { showErrorToast } from "@/shared/toast"

import { useTabbioData } from "./use_tabbio_data"

const STYLES = {
  loadingContainer: "flex justify-center items-center min-h-screen",
  errorWrapper: "p-8",
  notFound: "p-8",
  form: "flex flex-col gap-6 max-w-4xl",
  title: "text-3xl font-bold mb-0",
  closeIcon: "w-5 h-5",
  actionsRow: "flex justify-end gap-3",
} as const

const validationSchema = Yup.object({
  venueName: Yup.string().required("Venue name is required"),
  venueLogo: Yup.mixed().nullable(),
  description: Yup.string(),
  address: Yup.string(),
  city: Yup.string(),
  zipcode: Yup.string(),
  socialAccounts: Yup.object().shape({
    facebook: Yup.string().url("Must be a valid URL"),
    instagram: Yup.string().url("Must be a valid URL"),
    x: Yup.string().url("Must be a valid URL"),
    tiktok: Yup.string().url("Must be a valid URL"),
  }),
  phone: Yup.string(),
  email: Yup.string().email("Must be a valid email"),
  website: Yup.string().url("Must be a valid URL"),
})

const toSections = (
  sections?: Array<{ id: string; identifier: string; title: string; content?: string | null }> | null,
): Section[] => {
  return (
    sections?.map((section) => ({
      id: section.id,
      identifier: section.identifier,
      title: section.title,
      content: section.content || "",
    })) || []
  )
}

const VenueTabbio: React.FC = () => {
  const { venueSlug } = useParams()
  const navigate = useNavigate()
  const linkTo = usePanelLink()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [sections, setSections] = useState<Section[]>([])
  const [sectionsChanged, setSectionsChanged] = useState(false)

  const {
    venue,
    loading,
    error,
    refetch,
    customSectionsEnabled,
    googleMapsApiKey,
    saving,
    savingSections,
    updateVenueMutation,
    updateReservationRuleSectionsMutation,
  } = useTabbioData(venueSlug)

  const isSaving = saving || savingSections

  useEffect(() => {
    if (venue && !venue.permissions?.manageVenue) {
      navigate(linkTo("settings"))
    }
  }, [venue?.permissions?.manageVenue, navigate, linkTo])

  useEffect(() => {
    if (venue?.reservationRuleSections) {
      setSections(toSections(venue.reservationRuleSections))
      setSectionsChanged(false)
    }
  }, [venue?.reservationRuleSections])

  const handleSectionsChange = (newSections: Section[]) => {
    setSections(newSections)
    setSectionsChanged(true)
  }

  const handleSubmit = async (values) => {
    if (!venue) return

    setSuccessMessage(null)
    setErrorMessage(null)

    const shouldSaveVenue =
      values.venueName !== (venue.name || "") ||
      values.venueLogo !== null ||
      values.description !== (venue.info?.description || "") ||
      values.address !== (venue.info?.address || "") ||
      values.city !== (venue.info?.city || "") ||
      values.zipcode !== (venue.info?.zipcode || "") ||
      values.phone !== (venue.info?.phone || "") ||
      values.email !== (venue.info?.email || "") ||
      values.website !== (venue.info?.website || "") ||
      JSON.stringify(values.socialAccounts || {}) !==
        JSON.stringify((venue.info?.socialAccounts as Record<string, string>) || {}) ||
      values.latitude !== (venue.info?.latitude ?? null) ||
      values.longitude !== (venue.info?.longitude ?? null)

    try {
      let venueSaved = false
      let sectionsSaved = false

      if (shouldSaveVenue) {
        const result = await updateVenueMutation({
          variables: {
            venueId: venue.id,
            name: values.venueName,
            venueLogo: values.venueLogo,
            description: values.description,
            address: values.address,
            city: values.city,
            zipcode: values.zipcode,
            socialAccounts: values.socialAccounts,
            phone: values.phone,
            email: values.email,
            website: values.website,
            latitude: values.latitude,
            longitude: values.longitude,
          },
        })

        if (!result.data?.updateVenue?.success) {
          const errors = result.data?.updateVenue?.errors
          const errorCode = errors?.[0]?.code
          const errorKey = errorCode
            ? `app.settings.venues.tabbio.errors.${errorCode.toLowerCase()}`
            : "app.settings.venues.tabbio.errors.unknown"
          setErrorMessage(i18n.t(errorKey))
          return
        }

        venueSaved = true
      }

      if (sectionsChanged) {
        const pendingSections = sections
        const sectionsResult = await updateReservationRuleSectionsMutation({
          variables: {
            venueId: venue.id,
            sections: sections.map((section) => ({
              id: section.id,
              title: section.title,
              content: section.content,
            })),
          },
        })

        if (!sectionsResult.data?.updateReservationRuleSections?.success) {
          if (venueSaved) {
            await refetch()
            setSections(pendingSections)
            setSectionsChanged(true)
          }

          const errors = sectionsResult.data?.updateReservationRuleSections?.errors
          const errorCode = errors?.[0]?.code
          const errorKey = errorCode
            ? `app.settings.venues.tabbio.errors.${errorCode.toLowerCase()}`
            : "app.settings.venues.tabbio.errors.unknown"
          setErrorMessage(i18n.t(errorKey))
          return
        }

        sectionsSaved = true
      }

      if (venueSaved || sectionsSaved) {
        await refetch()
        setSuccessMessage(i18n.t("app.settings.venues.tabbio.success"))
      }
    } catch (err) {
      showErrorToast()
      console.error("Error updating venue:", err)
      setErrorMessage(i18n.t("app.settings.venues.tabbio.errors.generic"))
    }
  }

  if (loading) {
    return (
      <div className={STYLES.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div className={STYLES.errorWrapper}>
        <Alert severity="error">Error loading venue: {error.message}</Alert>
      </div>
    )
  }

  if (!venue) {
    return <div className={STYLES.notFound}>Venue not found</div>
  }

  return (
    <Formik
      initialValues={{
        venueName: venue?.name || "",
        venueLogo: null,
        description: venue?.info?.description || "",
        address: venue?.info?.address || "",
        city: venue?.info?.city || "",
        zipcode: venue?.info?.zipcode || "",
        socialAccounts: (venue?.info?.socialAccounts as Record<string, string>) || {},
        phone: venue?.info?.phone || "",
        email: venue?.info?.email || "",
        website: venue?.info?.website || "",
        latitude: venue?.info?.latitude ?? null,
        longitude: venue?.info?.longitude ?? null,
      }}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} className={STYLES.form} data-testid="settings-venue-tabbio-form">
          <h1 className={STYLES.title} data-testid="settings-venue-tabbio-title">
            {i18n.t("app.settings.venues.tabbio.title")}
          </h1>

          {!!successMessage && (
            <Alert
              severity="success"
              data-testid="settings-venue-tabbio-alert-success"
              action={
                <IconButton aria-label="close" color="inherit" size="small" onClick={() => setSuccessMessage(null)}>
                  <X className={STYLES.closeIcon} />
                </IconButton>
              }
            >
              {successMessage}
            </Alert>
          )}

          {!!errorMessage && (
            <Alert
              severity="error"
              data-testid="settings-venue-tabbio-alert-error"
              action={
                <IconButton aria-label="close" color="inherit" size="small" onClick={() => setErrorMessage(null)}>
                  <X className={STYLES.closeIcon} />
                </IconButton>
              }
            >
              {errorMessage}
            </Alert>
          )}

          <VenueLogo venue={venue} />
          <BasicInfo />
          <VenuePhotos venue={venue} onRefetch={refetch} />
          <Description />
          <ReservationRuleSections
            sections={sections}
            onChange={handleSectionsChange}
            customSectionsEnabled={customSectionsEnabled}
          />
          <Location apiKey={googleMapsApiKey} venueCountry={venue?.country} />
          <SocialAccounts />
          <ContactInformation />

          <div className={STYLES.actionsRow}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                formik.resetForm()
                setSections(toSections(venue.reservationRuleSections))
                setSectionsChanged(false)
              }}
              disabled={isSaving || (!formik.dirty && !sectionsChanged)}
              data-testid="settings-venue-tabbio-btn-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isSaving || !formik.isValid || (!formik.dirty && !sectionsChanged)}
              data-testid="settings-venue-tabbio-btn-save"
            >
              {isSaving ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  )
}

export default VenueTabbio
